<script setup lang="ts">
import type { ICreditCard } from '~/components/entity/credit/item/types'
import type { IEntityPosition } from '~/components/entity/base-entity'
import { Badge } from '~/components/ui/badge'
import { UiCardTop } from '~/components/ui/card'
import { LucideScale, LucideX } from 'lucide-vue-next'
import { Rating } from '~/components/ui/rating'
import { EntityOrganizationLogo } from '~/components/entity/organization-logo'

const props = withDefaults(
  defineProps<{
    item: ICreditCard
    hideOrganizationDetails?: boolean
    hideCompare?: boolean
    showTypes?: boolean
    type?: 'bank' | 'microcredit'
    position?: IEntityPosition
    selectedCurrency?: string
  }>(),
  {
    hideOrganizationDetails: false,
    hideCompare: false,
    showTypes: false,
    type: 'bank',
    position: undefined,
    selectedCurrency: undefined
  }
)

const { t } = useI18n()

const normalizedSelectedCurrency = computed(() => props.selectedCurrency?.toLowerCase())

// Islamic products use "financing" wording instead of "credit"
const isIslamicCredit = computed(() => !!props.item.is_islamic)

const isHighlighted = useEntityHighlight(props.item, props.position)

const creditTypes = computed(() => {
  return props.item.types?.map((type) => type.title) || []
})

const compareStore = useCompareStore()
const isInCompare = computed(() => compareStore.has('credits', props.item.id))
const atLimit = computed(() => compareStore.countByType.credits >= 10)

const toggleCompare = () => {
  if (isInCompare.value) {
    compareStore.remove('credits', props.item.id)
  } else {
    compareStore.add('credits', props.item.id)
  }
}

const compareLabel = computed(() => (isInCompare.value ? t('compare.remove') : t('compare.add')))

const formattedMonthlyPayment = computed(() => {
  if (!props.item.monthlyPayment) return null
  return useCurrencyFormat(
    props.item.monthlyPayment.amount,
    props.item.monthlyPayment.currencyId,
    true
  )
})

// The title may carry the search highlight (<em>); attributes need plain text
const plainTitle = computed(() => useStripTags(props.item.title))
</script>

<template>
  <article :class="[isHighlighted ? 'CardPremium group' : 'Card']">
    <UiCardTop v-if="isHighlighted" />
    <div class="EntityCardLayout">
      <div class="flex gap-4">
        <EntityOrganizationLogo
          v-if="!props.hideOrganizationDetails"
          :src="props.item.organization.logo"
          :alt="props.item.organization.title"
          :to="props.item.organization.url"
          :aria-label="
            t('entity.credit.item.more_about_org', { org: props.item.organization.title })
          "
          :highlighted="isHighlighted"
        />
        <div class="flex flex-col items-start">
          <NuxtLinkLocale
            :to="props.item.url"
            :aria-label="
              t(
                isIslamicCredit
                  ? 'entity.credit.item.more_about_credit_from_islamic'
                  : 'entity.credit.item.more_about_credit_from',
                {
                  title: plainTitle,
                  org: props.item.organization.title
                }
              )
            "
            class="inline-block hover:underline"
          >
            <Heading tag="h3" class="hit-title h6"><span v-hydrated-html="props.item.title" /></Heading>
          </NuxtLinkLocale>
          <NuxtLinkLocale
            v-if="!props.hideOrganizationDetails"
            :to="props.item.organization.url"
            :aria-label="
              t('entity.credit.item.more_about_org', { org: props.item.organization.title })
            "
            class="CardOrgLink"
          >
            {{ props.item.organization.title }}
          </NuxtLinkLocale>
          <div v-if="props.item.ratings" class="flex items-center">
            <Rating :value="Number(props.item.ratings)" size="xs" />
            <div class="ml-2 text-sm text-gray-900">{{ props.item.ratings }}</div>
          </div>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-y-2 sm:grid-cols-3">
        <div class="sm:col-span-1">
          <div class="SecondaryTextHeadings">{{ t('common.annual_rate') }}</div>
          <div class="font-semibold">{{ props.item.min_rate }}</div>
        </div>
        <div class="sm:col-span-1">
          <div class="SecondaryTextHeadings">{{ t('common.term') }}</div>
          <div class="font-semibold">{{ props.item.max_period ?? '&mdash;' }}</div>
        </div>
        <div
          v-if="props.item.currencies && props.item.currencies.length"
          class="col-span-2 sm:col-span-1"
        >
          <div class="SecondaryTextHeadings">{{ t('common.currency') }}</div>
          <div class="font-semibold">
            <template v-for="(currency, index) in props.item.currencies" :key="currency.code">
              <span
                :class="{
                  'text-blue-600':
                    normalizedSelectedCurrency &&
                    currency.code.toLowerCase() === normalizedSelectedCurrency
                }"
                >{{ currency.code }}</span
              ><span v-if="index < props.item.currencies.length - 1">, </span>
            </template>
          </div>
        </div>
      </div>
    </div>
    <footer class="mt-4 flex flex-wrap items-center gap-2">
      <div v-if="formattedMonthlyPayment" class="flex flex-col">
        <span class="SecondaryTextHeadings">{{ t('common.monthly_payment_approx') }}</span>
        <span class="font-semibold text-blue-600">{{ formattedMonthlyPayment }}</span>
      </div>
      <div class="flex flex-1 flex-wrap gap-2">
        <template v-if="creditTypes.length && (isHighlighted || showTypes)">
          <Badge
            v-for="creditType in creditTypes"
            :key="creditType"
            variant="gray"
            class="text-xs"
            rounded="full"
          >
            {{ creditType }}
          </Badge>
        </template>
      </div>

      <div v-if="!hideCompare" class="flex sm:-mt-1">
        <Button
          variant="ghost"
          size="sm"
          :disabled="!isInCompare && atLimit"
          class="CompareButton"
          @click="toggleCompare"
        >
          <LucideX v-show="isInCompare" class="size-3.5" />
          <LucideScale v-show="!isInCompare" class="size-3.5" />
          {{ compareLabel }}
        </Button>
      </div>
    </footer>
  </article>
</template>
